import React from 'react';

import SearchInput from 'components/search_sidebar/sidebar_search';
import FilterButton from 'components/ui/filter_button';
import FilterCheckboxGroup from 'components/search_sidebar/filter_checkbox_group';
import ResponsiveFilterContainer from 'components/search_page/responsive_filter_container';
import CurrentFilters from 'components/search_page/current_filters';
import Tag from 'components/tags/tag';
import type { FilterOption } from './types';

export type { FilterOption };

export const FILTER_KEYS = [
  'documentType', 'availability', 'propertyType', 'usersAndTeams', 'tags', 'createdBy'
] as const;

export type FilterKey = typeof FILTER_KEYS[number];
export type Filters = Record<FilterKey, string[]>;
export type FilterOptions = Partial<Record<FilterKey, FilterOption[]>>;

// Long lists get a search box in their popover
const SEARCHABLE_AFTER = 10;

interface Props {
  filterOptions: FilterOptions;
  filters: Filters;
  searchText: string;
  onFilterChange: (key: FilterKey, values: string[]) => void;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchClear?: () => void;
}

export default function OptionsFilters({
  filterOptions,
  filters,
  searchText,
  onFilterChange,
  onSearchChange,
  onSearchClear,
}: Props) {
  const labels: Record<FilterKey, string> = {
    documentType: 'Document Type',
    availability: 'Availability',
    propertyType: 'Property Type',
    usersAndTeams: 'Users',
    tags: 'Tags',
    createdBy: 'Created By',
  };

  const optionsFor = (key: FilterKey) => filterOptions[key] || [];

  const handleToggle = (key: FilterKey, value: string) => {
    const selected = filters[key];
    onFilterChange(key, selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  const handleClearFilters = () => {
    FILTER_KEYS.forEach(key => onFilterChange(key, []));
  };

  const renderAppliedFilters = () => {
    const tags: React.ReactElement[] = [];

    FILTER_KEYS.forEach((key) => {
      filters[key].forEach((value) => {
        const option = optionsFor(key).find(o => o.value === value);
        if (!option) return;

        tags.push(
          <Tag
            key={`${key}-${value}`}
            name={value}
            removable
            onRemove={() => onFilterChange(key, filters[key].filter(v => v !== value))}
          >
            {labels[key]}: {option.label}
          </Tag>
        );
      });
    });

    if (tags.length === 0) return null;

    return (
      <div className="mt-3">
        <CurrentFilters raw onClearFilters={handleClearFilters}>
          {tags}
        </CurrentFilters>
      </div>
    );
  };

  return (
    <div className="mb-3">
      <ResponsiveFilterContainer>
        <SearchInput
          placeholder="Search Documents"
          value={searchText}
          onChange={onSearchChange}
          onClear={onSearchClear}
        />

        {FILTER_KEYS.map(key => (
          optionsFor(key).length > 0 && (
            <FilterButton
              filtersApplied={filters[key].length > 0}
              key={key}
              label={labels[key]}
              name={`${key}Filter`}
              popoverBodyClassName="p-3"
            >
              <FilterCheckboxGroup
                maxNumberOfOptions={SEARCHABLE_AFTER}
                options={optionsFor(key)}
                selected={filters[key]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleToggle(key, e.target.value)}
              />
            </FilterButton>
          )
        ))}
      </ResponsiveFilterContainer>

      {renderAppliedFilters()}
    </div>
  );
}
